<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import campanhasReportAPI from 'dashboard/api/campanhasReport';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import ConversationLink from '../analytics/ConversationLink.vue';
import { useAnalyticsFormat } from '../analytics/useAnalyticsFormat';
import { campanhasLabel } from './campanhasLabels';

// As conversas de uma linha da campanha, de 25 em 25, pedidas só quando a
// linha é aberta, com o recorte da página (no molde do CaminhoConversas). Cada
// uma abre a conversa. Sem telefone: a lista não o pede, não o mostra e não o
// exporta. Quem falhou ou pediu para parar sem chegar a ter mensagem conta no
// número do resumo e não tem conversa: a lista diz quantos são.
const props = defineProps({
  // O recorte da API (`campanhasApiFilters`).
  filters: { type: Object, default: () => ({}) },
  timezone: { type: String, default: undefined },
});

const { t } = useI18n();
const { number, dateTime } = useAnalyticsFormat();

const dialogRef = ref(null);
const line = ref('');
const label = ref('');
const page = ref(1);
const result = ref(null);
const loading = ref(false);
const failed = ref(false);
let lastRequest = 0;

const load = async () => {
  lastRequest += 1;
  const request = lastRequest;
  loading.value = true;
  failed.value = false;
  try {
    const { data } = await campanhasReportAPI.conversas(
      props.filters,
      line.value,
      page.value
    );
    if (request !== lastRequest) return;
    result.value = data;
  } catch {
    if (request !== lastRequest) return;
    result.value = null;
    failed.value = true;
  } finally {
    if (request === lastRequest) loading.value = false;
  }
};

const open = (key, lineLabel) => {
  line.value = key;
  label.value = lineLabel || campanhasLabel(t, 'line', key);
  page.value = 1;
  result.value = null;
  dialogRef.value?.open();
  load();
};

const goTo = next => {
  page.value = next;
  load();
};

const total = computed(() => result.value?.total || 0);
const pages = computed(() =>
  Math.max(Math.ceil(total.value / (result.value?.per_page || 25)), 1)
);
const conversations = computed(() => result.value?.conversations || []);
const withoutConversation = computed(
  () => result.value?.without_conversation || 0
);

// "Enviada 20/09, 10:00 · Lida · respondeu 20/09, 10:30 · a IA atendeu".
const detail = item =>
  [
    item.inbox,
    item.sent_at
      ? t('CAMPANHAS.CONVERSATIONS.SENT_AT', {
          time: dateTime(item.sent_at, props.timezone),
        })
      : '',
    campanhasLabel(t, 'delivery', item.delivery_status),
    item.replied_at
      ? t('CAMPANHAS.CONVERSATIONS.REPLIED_AT', {
          time: dateTime(item.replied_at, props.timezone),
        })
      : '',
    item.first_response?.by
      ? t('CAMPANHAS.CONVERSATIONS.ANSWERED_BY', {
          who: campanhasLabel(t, 'after', item.first_response.by),
        })
      : '',
    item.handed_off ? t('CAMPANHAS.CONVERSATIONS.HANDED_OFF') : '',
  ]
    .filter(Boolean)
    .join(' · ');

defineExpose({ open });
</script>

<template>
  <Dialog
    ref="dialogRef"
    width="2xl"
    overflow-y-auto
    :title="
      t(
        'CAMPANHAS.CONVERSATIONS.TITLE',
        { line: label, count: number(total) },
        total
      )
    "
    :show-confirm-button="false"
    :cancel-button-label="t('CAMPANHAS.CONVERSATIONS.CLOSE')"
  >
    <div
      v-if="loading && !result"
      class="flex gap-2 justify-center items-center py-8 text-sm text-n-slate-11"
    >
      <Spinner :size="16" />
    </div>
    <p v-else-if="failed" class="mb-0 text-sm text-n-ruby-11">
      {{ t('CAMPANHAS.CONVERSATIONS.FAILED') }}
    </p>
    <template v-else-if="result">
      <p
        v-if="withoutConversation"
        data-test-id="campanha-conversas-sem-conversa"
        class="px-3 py-2 mb-0 text-xs rounded-lg bg-n-alpha-2 text-n-slate-11"
      >
        {{
          t(
            'CAMPANHAS.CONVERSATIONS.WITHOUT_CONVERSATION',
            { count: number(withoutConversation) },
            withoutConversation
          )
        }}
      </p>
      <p v-if="!conversations.length" class="mb-0 text-sm text-n-slate-11">
        {{ t('CAMPANHAS.CONVERSATIONS.EMPTY') }}
      </p>
      <ul
        v-else
        data-test-id="campanha-conversas-lista"
        class="flex flex-col list-none divide-y divide-n-weak"
        :class="{ 'opacity-60': loading }"
      >
        <li
          v-for="item in conversations"
          :key="`${item.display_id}-${item.sent_at}`"
          class="flex flex-wrap gap-x-3 gap-y-0.5 justify-between items-baseline py-2 min-w-0 text-sm"
        >
          <span class="flex flex-col min-w-0">
            <span class="truncate text-n-slate-12">
              {{ item.contact_name || t('CAMPANHAS.CONVERSATIONS.NO_CONTACT') }}
            </span>
            <span class="text-xs text-n-slate-11">{{ detail(item) }}</span>
          </span>
          <ConversationLink :conversation-id="item.display_id" />
        </li>
      </ul>
      <div
        v-if="pages > 1"
        class="flex gap-2 justify-between items-center text-xs text-n-slate-11"
      >
        <Button
          :label="t('CAMPANHAS.CONVERSATIONS.PREVIOUS')"
          xs
          slate
          faded
          type="button"
          :disabled="page <= 1 || loading"
          @click="goTo(page - 1)"
        />
        <span>
          {{ t('CAMPANHAS.TABLE.PAGE', { page, pages }) }}
        </span>
        <Button
          :label="t('CAMPANHAS.CONVERSATIONS.NEXT')"
          xs
          slate
          faded
          type="button"
          :disabled="page >= pages || loading"
          @click="goTo(page + 1)"
        />
      </div>
    </template>
  </Dialog>
</template>
